/**
 * DecisionStore — the Decision Log feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY decisions module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Decision>('decisions')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model: each decision is one document with id `dec_<uuid>` in the
 * real 'decisions' generic-store collection (DDB pointer row + S3 body). Every
 * field is short, so every field is mirrored onto the DDB pointer row's index
 * attributes and {@link listSummaries} reconstructs full decision summaries
 * directly from the pointer rows WITHOUT hydrating S3 bodies.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Decision, DecisionDraft, DecisionSummary } from './types';
import { decisionDocId } from './decisionModel';

export const DECISIONS_COLLECTION = 'decisions';

/** Delimiter used to mirror string[] fields onto a single pointer-row attribute. */
const LIST_SEP = '\n';

const joinList = (list?: string[]): string => (list ?? []).join(LIST_SEP);
const splitList = (raw?: string): string[] =>
  (raw ?? '')
    .split(LIST_SEP)
    .map((s) => s.trim())
    .filter(Boolean);

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  decision: 'decision',
  context: 'context',
  optionsConsidered: 'optionsConsidered',
  decisionMade: 'decisionMade',
  why: 'why',
  owner: 'owner',
  participants: 'participants',
  date: 'date',
  affectedServices: 'affectedServices',
  relatedDocId: 'relatedDocId',
  relatedGoalId: 'relatedGoalId',
  relatedWorkstreamId: 'relatedWorkstreamId',
  relatedStoryIds: 'relatedStoryIds',
  revisitDate: 'revisitDate',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class DecisionStore {
  private readonly docs: DocumentStore<Decision>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Decision>(DECISIONS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(decision: Decision): StringMap {
    return {
      [ROW.decision]: decision.decision ?? '',
      [ROW.context]: decision.context ?? '',
      [ROW.optionsConsidered]: joinList(decision.optionsConsidered),
      [ROW.decisionMade]: decision.decisionMade ?? '',
      [ROW.why]: decision.why ?? '',
      [ROW.owner]: decision.owner ?? '',
      [ROW.participants]: joinList(decision.participants),
      [ROW.date]: decision.date ?? '',
      [ROW.affectedServices]: joinList(decision.affectedServices),
      [ROW.relatedDocId]: decision.relatedDocId ?? '',
      [ROW.relatedGoalId]: decision.relatedGoalId ?? '',
      [ROW.relatedWorkstreamId]: decision.relatedWorkstreamId ?? '',
      [ROW.relatedStoryIds]: joinList(decision.relatedStoryIds),
      [ROW.revisitDate]: decision.revisitDate ?? '',
      [ROW.createdBy]: decision.createdBy ?? '',
      [ROW.createdAt]: decision.createdAt ?? '',
      [ROW.lastUpdated]: decision.lastUpdated ?? '',
      [ROW.deleted]: decision.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full decision summary from a pointer row. */
  private toSummary(row: StringMap): DecisionSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      decision: row[ROW.decision] ?? '',
      context: row[ROW.context] ?? '',
      optionsConsidered: splitList(row[ROW.optionsConsidered]),
      decisionMade: row[ROW.decisionMade] ?? '',
      why: row[ROW.why] ?? '',
      owner: row[ROW.owner] ?? '',
      participants: splitList(row[ROW.participants]),
      date: row[ROW.date] ?? '',
      affectedServices: splitList(row[ROW.affectedServices]),
      relatedDocId: row[ROW.relatedDocId] || undefined,
      relatedGoalId: row[ROW.relatedGoalId] || undefined,
      relatedWorkstreamId: row[ROW.relatedWorkstreamId] || undefined,
      relatedStoryIds: splitList(row[ROW.relatedStoryIds]),
      revisitDate: row[ROW.revisitDate] || undefined,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /** Lists all non-deleted decision summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<DecisionSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === DECISIONS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single decision document by id (hydrates the S3 body). */
  async get(id: string): Promise<Decision | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new decision. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, and returns the saved document.
   */
  async create(draft: DecisionDraft): Promise<Decision> {
    const identity = await this.api.identityProvider.getIdentity();
    const decision = this.fromDraft(decisionDocId(), draft, identity.alias, undefined);
    await this.docs.save(decision.id, decision, this.indexAttributes(decision));
    return decision;
  }

  /**
   * Applies a draft's fields to an existing decision in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, and re-mirrors index attributes.
   */
  async applyDraft(decision: Decision, draft: DecisionDraft): Promise<Decision> {
    const updated = this.fromDraft(
      decision.id,
      draft,
      decision.createdBy,
      decision.createdAt,
    );
    return this.update(updated);
  }

  /** Persists a full decision document in place (refreshes lastUpdated). */
  async update(decision: Decision): Promise<Decision> {
    const updated: Decision = {
      ...decision,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a decision: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Decision = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /**
   * Builds a full Decision from a draft, seeding id/createdBy and preserving
   * createdAt on edit (new entries stamp "now"). Empty optional text fields
   * collapse to undefined.
   */
  private fromDraft(
    id: string,
    draft: DecisionDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): Decision {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      decision: draft.decision.trim(),
      context: draft.context.trim(),
      optionsConsidered: draft.optionsConsidered ?? [],
      decisionMade: draft.decisionMade.trim(),
      why: draft.why.trim(),
      owner: draft.owner.trim(),
      participants: draft.participants ?? [],
      date: draft.date.trim(),
      affectedServices: draft.affectedServices ?? [],
      relatedDocId: draft.relatedDocId || undefined,
      relatedGoalId: draft.relatedGoalId || undefined,
      relatedWorkstreamId: draft.relatedWorkstreamId || undefined,
      relatedStoryIds: draft.relatedStoryIds ?? [],
      revisitDate: clean(draft.revisitDate),
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
